import React, { useCallback, useEffect, useState } from 'react';
import {
    Card, CardTitle, CardHeaderRow, Button, Pill, Notice,
    DataTable, TableScroll, EmptyState, SpinnerContainer, Spinner,
} from '../ui';
import { getJobs, getJobsJournal, postJobRun, postJobSchedule } from '../../api/internal/usersInternalApi';

// Seule source de fraîcheur de la page. 20 s est le bon ordre de grandeur pour
// des collectes qui durent de plusieurs minutes à plusieurs heures, et c'est
// aussi la cadence à laquelle Agenda lui-même interroge la file.
// Une action de l'utilisateur — lancer, activer, désactiver — recharge
// immédiatement, sans attendre le tour suivant.
const RAFRAICHISSEMENT_MS = 20000;

const TONS_ETAT = {
    'en cours': 'accent',
    'succès': 'ok',
    'échec': 'danger',
    'planifiée': 'neutral',
    'jamais exécutée': 'neutral',
};

function dateCourte(valeur) {
    if (!valeur) return '—';
    return new Date(valeur).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
}

function duree(secondes) {
    if (secondes === null || secondes === undefined) return '—';
    if (secondes < 60) return `${secondes} s`;
    const minutes = Math.floor(secondes / 60);
    if (minutes < 60) return `${minutes} min ${String(secondes % 60).padStart(2, '0')} s`;
    return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
}

// Une collecte lancée va au bout : le bouton n'a pas de bouton d'annulation en
// face, donc la confirmation annonce la durée attendue plutôt que de laisser
// l'admin découvrir 2 h 45 de balayage après coup.
function confirmerLancement(tache) {
    const duree = tache.dureeIndicative ? ` Durée attendue : ${tache.dureeIndicative}.` : '';
    return window.confirm(
        `Lancer « ${tache.libelle} » maintenant ?${duree}\n\n`
        + 'La tâche sera mise en file et ne pourra pas être interrompue une fois démarrée.'
    );
}

export default function Jobs() {
    const [taches, setTaches] = useState([]);
    const [journal, setJournal] = useState([]);
    const [chargement, setChargement] = useState(true);
    const [erreur, setErreur] = useState('');
    const [message, setMessage] = useState('');
    const [enAttente, setEnAttente] = useState(null);
    const [groupeOuvert, setGroupeOuvert] = useState('Passages');

    const charger = useCallback(async (signal) => {
        try {
            const [etat, logs] = await Promise.all([
                getJobs(signal),
                getJobsJournal(60, null, signal),
            ]);
            if (etat) setTaches(etat.taches || []);
            if (logs) setJournal(logs.entrees || []);
            setErreur('');
        } catch (e) {
            setErreur(e?.response?.status === 401
                ? "Cette page est réservée aux administrateurs."
                : "Impossible de récupérer l'état des tâches.");
        } finally {
            setChargement(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        charger(controller.signal);
        const minuteur = setInterval(() => charger(), RAFRAICHISSEMENT_MS);
        return () => { controller.abort(); clearInterval(minuteur); };
    }, [charger]);

    const lancer = async (tache) => {
        if (!confirmerLancement(tache)) return;
        setEnAttente(tache.id);
        try {
            const reponse = await postJobRun(tache.id);
            setMessage(reponse.message);
            await charger();
        } catch (e) {
            setErreur(e.error || 'Le lancement a échoué.');
        } finally {
            setEnAttente(null);
        }
    };

    const basculer = async (tache) => {
        setEnAttente(tache.id);
        try {
            await postJobSchedule(tache.id, !tache.active);
            setMessage(`Planification « ${tache.libelle} » ${tache.active ? 'désactivée' : 'activée'}`);
            await charger();
        } catch (e) {
            setErreur(e.error || 'La modification a échoué.');
        } finally {
            setEnAttente(null);
        }
    };

    if (chargement) {
        return <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>;
    }

    const groupes = [...new Set(taches.map((t) => t.groupe))];
    const enCours = taches.filter((t) => t.enCours);

    return (
        <>
            {erreur && <Notice $tone="danger"><i className="bx bx-error-circle" />{erreur}</Notice>}
            {message && <Notice $tone="ok"><i className="bx bx-check-circle" />{message}</Notice>}

            {enCours.length > 0 && (
                <Notice $tone="accent">
                    <i className="bx bx-loader-alt bx-spin" />
                    {enCours.map((t) => (
                        <span key={t.id}>
                            <strong>{t.libelle}</strong> en cours depuis {dateCourte(t.depuis)}
                            {t.avancement != null ? ` — ${t.avancement} %` : ''}
                        </span>
                    ))}
                </Notice>
            )}

            {taches.some((t) => t.planifiee && !t.active) && (
                <Notice $tone="warn">
                    <i className="bx bx-pause-circle" />
                    Des planifications sont désactivées : les collectes correspondantes ne partiront plus tant qu'elles ne sont pas réactivées.
                </Notice>
            )}

            {groupes.map((groupe) => {
                const duGroupe = taches.filter((t) => t.groupe === groupe);
                const ouvert = groupeOuvert === groupe;
                return (
                    <Card key={groupe}>
                        <CardHeaderRow>
                            <CardTitle
                                onClick={() => setGroupeOuvert(ouvert ? null : groupe)}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className={`bx bx-chevron-${ouvert ? 'down' : 'right'}`} />
                                {groupe}
                                <Pill $tone="neutral" style={{ marginLeft: 8 }}>{duGroupe.length}</Pill>
                            </CardTitle>
                        </CardHeaderRow>

                        {ouvert && (
                            <TableScroll>
                                <DataTable>
                                    <thead>
                                        <tr>
                                            <th>Tâche</th>
                                            <th>Planification</th>
                                            <th>Prochaine</th>
                                            <th>Dernière</th>
                                            <th>Durée</th>
                                            <th>État</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {duGroupe.map((tache) => (
                                            <tr key={tache.id}>
                                                <td>
                                                    <strong>{tache.libelle}</strong>
                                                    <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                                        {tache.description}
                                                    </div>
                                                </td>
                                                <td>
                                                    {tache.planifiee
                                                        ? <code style={{ fontSize: '0.75rem' }}>{tache.cron}</code>
                                                        : <span style={{ opacity: 0.6 }}>à la demande</span>}
                                                </td>
                                                <td>
                                                    {tache.planifiee
                                                        ? (tache.active ? dateCourte(tache.prochaineExecution) : <Pill $tone="warn">désactivée</Pill>)
                                                        : '—'}
                                                </td>
                                                <td>
                                                    {dateCourte(tache.derniereExecution)}
                                                    {tache.declenchePar && (
                                                        <div style={{ fontSize: '0.72rem', opacity: 0.7 }}>
                                                            par {tache.declenchePar}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{duree(tache.dureeSecondes)}</td>
                                                <td>
                                                    <Pill $tone={TONS_ETAT[tache.etat] || 'neutral'}>
                                                        {tache.etat}
                                                    </Pill>
                                                    {tache.dernierEchec && tache.etat === 'échec' && (
                                                        <div style={{ fontSize: '0.72rem', opacity: 0.8, maxWidth: 260 }}>
                                                            {tache.dernierEchec}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <Button
                                                            type="button"
                                                            $variant="ghost"
                                                            disabled={enAttente === tache.id || tache.enCours}
                                                            onClick={() => lancer(tache)}
                                                            title={tache.enCours ? 'Déjà en cours' : 'Lancer maintenant'}
                                                        >
                                                            <i className="bx bx-play" />
                                                            Lancer
                                                        </Button>
                                                        {tache.planifiee && (
                                                            <Button
                                                                type="button"
                                                                $variant="ghost"
                                                                disabled={enAttente === tache.id}
                                                                onClick={() => basculer(tache)}
                                                                title={tache.active
                                                                    ? "Désactiver la planification — n'interrompt pas une exécution en cours"
                                                                    : 'Réactiver la planification'}
                                                            >
                                                                <i className={`bx bx-${tache.active ? 'pause' : 'play-circle'}`} />
                                                                {tache.active ? 'Désactiver' : 'Activer'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DataTable>
                            </TableScroll>
                        )}
                    </Card>
                );
            })}

            <Card>
                <CardHeaderRow>
                    <CardTitle>Journal des exécutions</CardTitle>
                </CardHeaderRow>
                {journal.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-history" />
                        <h3>Aucune exécution enregistrée</h3>
                        <p>Le journal se remplit au premier passage.</p>
                    </EmptyState>
                ) : (
                    <TableScroll>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>Horodatage</th>
                                    <th>Tâche</th>
                                    <th>Événement</th>
                                    <th>Message</th>
                                </tr>
                            </thead>
                            <tbody>
                                {journal.map((entree, index) => (
                                    <tr key={`${entree.horodatage}-${index}`}>
                                        <td>{dateCourte(entree.horodatage)}</td>
                                        <td>{entree.libelle}</td>
                                        <td>
                                            {/* Valeurs produites par Agenda : start, success, fail, complete, locked. */}
                                            <Pill $tone={entree.niveau === 'error' ? 'danger' : entree.evenement === 'success' ? 'ok' : 'neutral'}>
                                                {entree.evenement}
                                            </Pill>
                                        </td>
                                        <td style={{ fontSize: '0.78rem' }}>{entree.message}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </DataTable>
                    </TableScroll>
                )}
            </Card>
        </>
    );
}
